import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { collection, addDoc, query, where, getDocs } from "firebase/firestore";
import "../App.css";
import logo from "../assets/logo.png";
import dashboardIcon from "../assets/layout-grid.png";
import appointmentsIcon from "../assets/Tick Square.png";
import logoutIcon from "../assets/logout.png";
import userIcon from "../assets/user_avatar.png";

const AddDoctor = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    fhirUrl: "",
    hospitalName: "",
    hospitalAddress: "",
    specialty: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("token");
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
      localStorage.removeItem("token");
      navigate("/login");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear errors when user starts typing
    if (error) setError("");
    if (success) setSuccess("");
  };

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.phone || !formData.hospitalName || !formData.specialty) {
      setError("Name, Email, Phone, Hospital Name, and Specialty are required");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Invalid email address");
      return false;
    }

    // Validate phone number format
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    if (!phoneRegex.test(formData.phone)) {
      setError("Invalid phone number format");
      return false;
    }

    // Validate FHIR URL format if provided
    if (formData.fhirUrl && formData.fhirUrl.trim() !== "") {
      try {
        new URL(formData.fhirUrl);
      } catch (e) {
        setError("Invalid FHIR URL format");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Check if doctor with same email already exists
      const doctorsRef = collection(db, "doctors");
      const q = query(doctorsRef, where("email", "==", formData.email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const overwrite = window.confirm(
          "A doctor with this email already exists. Do you want to add another entry?"
        );
        if (!overwrite) {
          setLoading(false);
          return;
        }
      }

      // Create doctor document
      const doctorData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        fhirUrl: formData.fhirUrl || null,
        hospitalName: formData.hospitalName,
        hospitalAddress: formData.hospitalAddress || null,
        specialty: formData.specialty,
        createdAt: new Date().toISOString(),
        isActive: true,
      };

      const docRef = await addDoc(doctorsRef, doctorData);
      console.log("✅ Doctor document created with ID:", docRef.id);

      setSuccess(`Doctor added successfully! Doctor ID: ${docRef.id}`);

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        fhirUrl: "",
        hospitalName: "",
        hospitalAddress: "",
        specialty: "",
      });
    } catch (error) {
      console.error("Error adding doctor:", error);
      let errorMessage = "Failed to add doctor. Please try again.";

      if (error.code === "permission-denied") {
        errorMessage = "Permission denied. Please check Firestore security rules.";
      } else if (error.code === "unavailable") {
        errorMessage = "Firestore is unavailable. Please check your connection.";
      } else {
        errorMessage = error.message || "Failed to add doctor. Please try again.";
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="appointments-container">
      {/* Sidebar */}
      <div className="sidebar">
        <img src={logo} alt="Timely Logo" className="logo-image" />
        <div className="user-profile">
          <div className="user-info">
            <h3>Kevin Beers</h3>
            <p>Support Team</p>
          </div>
        </div>
        <nav>
          <a href="/dashboard">
            <img src={dashboardIcon} alt="Dashboard" className="menu-icon" />
            Dashboard
          </a>
          <a href="/appointments">
            <img src={appointmentsIcon} alt="Appointments" className="menu-icon" />
            Appointments
          </a>
          <a href="/users">
            <img src={userIcon} alt="Users" className="menu-icon" />
            Users
          </a>
          <a href="/doctors">
            <span className="menu-icon" style={{ fontSize: "24px" }}>👨‍⚕️</span>
            Doctors
          </a>
          <a href="/add-doctor" className="active">
            <span className="menu-icon" style={{ fontSize: "24px" }}>👨‍⚕️</span>
            Add Doctor
          </a>
          <a href="/" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
            <img src={logoutIcon} alt="Log Out" className="menu-icon" />
            Log Out
          </a>
        </nav>
      </div>

      {/* Main Content */}
      <div className="appointments-page">
        <h1 className="dashboard-title">Add New Doctor</h1>

        {error && (
          <div
            className="error-message"
            style={{
              color: "#d32f2f",
              backgroundColor: "#ffebee",
              padding: "15px",
              borderRadius: "5px",
              marginBottom: "20px",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            className="success-message"
            style={{
              color: "#2e7d32",
              backgroundColor: "#e8f5e9",
              padding: "15px",
              borderRadius: "5px",
              marginBottom: "20px",
              fontSize: "14px",
            }}
          >
            {success}
          </div>
        )}

        <div
          style={{
            backgroundColor: "#ffffff",
            padding: "30px",
            borderRadius: "10px",
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
            maxWidth: "600px",
          }}
        >
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Doctor Name *</label>
              <input
                type="text"
                className="login-input"
                name="name"
                placeholder="Dr. John Smith"
                required
                value={formData.name}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Email *</label>
              <input
                type="email"
                className="login-input"
                name="email"
                placeholder="doctor@example.com"
                required
                value={formData.email}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Phone *</label>
              <input
                type="tel"
                className="login-input"
                name="phone"
                placeholder="+1 (555) 123-4567"
                required
                value={formData.phone}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">FHIR URL</label>
              <input
                type="url"
                className="login-input"
                name="fhirUrl"
                placeholder="https://fhir.example.com/Patient/123"
                value={formData.fhirUrl}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Hospital Name *</label>
              <input
                type="text"
                className="login-input"
                name="hospitalName"
                placeholder="General Hospital"
                required
                value={formData.hospitalName}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Hospital Address</label>
              <textarea
                className="login-input"
                name="hospitalAddress"
                placeholder="123 Main St, City, State, ZIP"
                rows="3"
                value={formData.hospitalAddress}
                onChange={handleInputChange}
                disabled={loading}
                style={{ resize: "vertical", minHeight: "80px" }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Specialty *</label>
              <input
                type="text"
                className="login-input"
                name="specialty"
                placeholder="Cardiology, Pediatrics, etc."
                required
                value={formData.specialty}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={loading}
              style={{
                opacity: loading ? 0.6 : 1,
                cursor: loading ? "not-allowed" : "pointer",
                width: "100%",
              }}
            >
              {loading ? "Adding Doctor..." : "Add Doctor"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddDoctor;

